import { useState, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { useToast } from "@/hooks/use-toast";
import type { Creator } from "@shared/schema";
import { getOptimizedPhotoUrl } from "@/lib/utils";
import { RedFlagBadge } from "@/components/ui/red-flag-badge";
import { RedFlagModal } from "@/components/modals/red-flag-modal";
import {
  Play,
  ExternalLink,
  Mail,
  Phone,
  Globe,
  MapPin,
  Calendar,
  User,
  Languages,
  Briefcase,
  Euro,
  Clock,
  Star,
  Camera,
  Video,
  Settings,
  Award,
  Heart,
  CheckCircle,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Copy,
  Link as LinkIcon,
  Folder,
  Flag,
  Building2,
  History,
  ChevronDown,
  ChevronUp,
  Sparkles,
  KeyRound,
} from "lucide-react";

interface DetailedCreatorProfileProps {
  creator: Creator;
}

type AssignmentSummary = {
  id: string;
  status: string;
  contactedAt: string | null;
  createdAt: string | null;
  script: { id: string; title: string; project: { id: string; title: string } };
};

type AssignmentsResponse = {
  total: number;
  assignments: AssignmentSummary[];
};

export default function DetailedCreatorProfile({ creator: initialCreator }: DetailedCreatorProfileProps) {
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showRedFlagModal, setShowRedFlagModal] = useState(false);
  const [showAllAssignments, setShowAllAssignments] = useState(false);
  const [bioExpanded, setBioExpanded] = useState(false);
  const [portalAccess, setPortalAccess] = useState<{ email: string; tempPassword?: string; linked?: boolean } | null>(null);
  const [creatingAccess, setCreatingAccess] = useState(false);
  const [resetAccess, setResetAccess] = useState<{ email: string; tempPassword: string } | null>(null);
  const [resettingPassword, setResettingPassword] = useState(false);
  const portfolioRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  const handleCreatePortalAccess = async () => {
    setCreatingAccess(true);
    try {
      const res = await fetch(`/api/admin/creators/${initialCreator.id}/create-portal-access`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message ?? 'Erreur');
      setPortalAccess(data);
    } catch (err: unknown) {
      toast({ title: 'Erreur', description: err instanceof Error ? err.message : 'Erreur inconnue', variant: 'destructive' });
    } finally {
      setCreatingAccess(false);
    }
  };

  const handleResetPassword = async () => {
    setResettingPassword(true);
    try {
      const res = await fetch(`/api/admin/creators/${initialCreator.id}/reset-password`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message ?? 'Erreur');
      setResetAccess(data);
    } catch (err: unknown) {
      toast({ title: 'Erreur', description: err instanceof Error ? err.message : 'Erreur inconnue', variant: 'destructive' });
    } finally {
      setResettingPassword(false);
    }
  };

  // Utiliser React Query pour récupérer les données à jour du créateur
  const { data: creator } = useQuery<Creator & { hasBeenContacted?: boolean }>({
    queryKey: [`/api/creators/${initialCreator.id}`],
    queryFn: async () => {
      const res = await fetch(`/api/creators/${initialCreator.id}`);
      if (!res.ok) throw new Error('Failed to fetch creator');
      return res.json();
    },
    initialData: initialCreator, // Utiliser les données initiales pour éviter un flash
    staleTime: 0, // Considérer comme périmé immédiatement pour refetch au changement
  });

  // Récupérer l'historique des collaborations (uniquement si contacté)
  const assignmentsLimit = showAllAssignments ? 50 : 3;
  const { data: assignmentsData } = useQuery<AssignmentsResponse>({
    queryKey: [`/api/creators/${initialCreator.id}/assignments`, assignmentsLimit],
    queryFn: async () => {
      const res = await fetch(`/api/creators/${initialCreator.id}/assignments?limit=${assignmentsLimit}`);
      if (!res.ok) throw new Error('Failed to fetch assignments');
      return res.json();
    },
  });

  console.log('[DETAILED CREATOR PROFILE] Creator data:', {
    id: creator.id,
    name: creator.name,
    isRedFlag: creator.isRedFlag,
    redFlagReason: creator.redFlagReason,
  });

  const handleCopy = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: "Copié !",
        description: `${type} copié dans le presse-papiers`,
      });
    } catch (err) {
      toast({
        title: "Erreur",
        description: "Impossible de copier dans le presse-papiers",
        variant: "destructive",
      });
    }
  };

  const scrollToPortfolio = () => {
    portfolioRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const getInitials = (name: string | null | undefined) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  const formatPrice = (price: number | null | undefined) => {
    if (price === null || price === undefined || price === 0) return "Non spécifié";
    return `${(price / 100).toLocaleString('fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })} €`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "approved":
        return "bg-success/20 text-success dark:bg-success/70 dark:text-green-200";
      case "pending":
        return "bg-warning/20 text-warning dark:bg-yellow-900 dark:text-yellow-200";
      case "rejected":
        return "bg-destructive/20 text-destructive dark:bg-destructive/70 dark:text-red-200";
      default:
        return "bg-muted text-foreground dark:bg-foreground/10 dark:text-gray-200";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "approved":
        return <CheckCircle className="h-4 w-4" />;
      case "pending":
        return <AlertCircle className="h-4 w-4" />;
      case "rejected":
        return <XCircle className="h-4 w-4" />;
      default:
        return <User className="h-4 w-4" />;
    }
  };

  const isBirthdaySoon = (birthDate: string | null | undefined): boolean => {
    if (!birthDate) return false;
    const today = new Date();
    const [, month, day] = birthDate.split('-').map(Number);
    const thisYearBirthday = new Date(today.getFullYear(), month - 1, day);
    const diffDays = Math.ceil((thisYearBirthday.getTime() - today.getTime()) / 86400000);
    return diffDays >= 0 && diffDays <= 14;
  };

  const formatBirthDate = (birthDate: string | null | undefined): string => {
    if (!birthDate) return '';
    const [year, month, day] = birthDate.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const formatDate = (date: Date | null | undefined) => {
    if (!date) return "Non spécifié";
    return new Date(date).toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  };

  const translateDelay = (delay: string | null | undefined) => {
    if (!delay || !delay.trim()) return "Non spécifié";
    
    let translated = delay.trim();
    
    // Traductions courantes anglais -> français
    const translations: Record<string, string> = {
      'day': 'jour',
      'days': 'jours',
      'hour': 'heure',
      'hours': 'heures',
      'week': 'semaine',
      'weeks': 'semaines',
      'month': 'mois',
      'months': 'mois',
      'business day': 'jour ouvrable',
      'business days': 'jours ouvrables',
      'working day': 'jour ouvré',
      'working days': 'jours ouvrés'
    };
    
    // Remplacer les termes anglais par leurs équivalents français
    for (const [en, fr] of Object.entries(translations)) {
      const regex = new RegExp(`\\b${en}\\b`, 'gi');
      translated = translated.replace(regex, fr);
    }
    
    return translated;
  };

  const parseVideoUrl = (url: string) => {
    const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const youtubeMatch = url.match(youtubeRegex);
    if (youtubeMatch) {
      return {
        platform: "youtube",
        videoId: youtubeMatch[1],
        thumbnail: `https://img.youtube.com/vi/${youtubeMatch[1]}/mqdefault.jpg`,
        embedUrl: `https://www.youtube.com/embed/${youtubeMatch[1]}`,
      };
    }

    const tiktokRegex = /tiktok\.com\/.*\/video\/(\d+)/;
    const tiktokMatch = url.match(tiktokRegex);
    if (tiktokMatch) {
      return {
        platform: "tiktok",
        videoId: tiktokMatch[1],
        thumbnail: null,
        embedUrl: url,
      };
    }

    const instagramRegex = /instagram\.com\/(?:p|reel)\/([a-zA-Z0-9_-]+)/;
    const instagramMatch = url.match(instagramRegex);
    if (instagramMatch) {
      return {
        platform: "instagram",
        videoId: instagramMatch[1],
        thumbnail: null,
        embedUrl: url,
      };
    }

    return {
      platform: "unknown",
      videoId: url,
      thumbnail: null,
      embedUrl: url,
    };
  };

  const getPlatformColor = (platform: string) => {
    switch (platform) {
      case "youtube":
        return "bg-destructive/20 text-destructive dark:bg-destructive/70 dark:text-red-200";
      case "tiktok":
        return "bg-black text-primary-foreground dark:bg-foreground/10";
      case "instagram":
        return "bg-gradient-to-r from-purple-500 to-pink-500 text-primary-foreground";
      default:
        return "bg-muted text-foreground dark:bg-foreground/10 dark:text-gray-200";
    }
  };

  const handleVideoClick = (url: string) => {
    setSelectedVideo(url);
    setShowVideoModal(true);
  };

  const ensureAbsoluteUrl = (url: string) => /^https?:\/\//.test(url) ? url : `https://${url}`;

  const handleExternalLink = (url: string) => {
    window.open(ensureAbsoluteUrl(url), "_blank", "noopener,noreferrer");
  };

  return (
    <div className="space-y-6" data-testid="detailed-creator-profile">
      {/* En-tête avec tarifs et délais intégrés */}
      <Card className="border-l-4 border-l-primary shadow-sm">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex flex-col items-center md:items-start">
              <div className="relative">
                <Avatar className="h-32 w-32 mb-4 ring-4 ring-primary/10" data-testid="avatar-creator">
                  <AvatarImage 
                    src={getOptimizedPhotoUrl(creator.photoUrl, 'medium', creator.id)} 
                    alt={creator.name}
                  />
                  <AvatarFallback className="text-2xl bg-primary/10 text-primary">
                    {getInitials(creator.name)}
                  </AvatarFallback>
                </Avatar>
                {creator.website && (
                  <button
                    onClick={() => handleExternalLink(creator.website!)}
                    className="absolute bottom-2 right-0 h-10 w-10 rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 transition-all flex items-center justify-center"
                    data-testid="button-open-portfolio"
                    title="Ouvrir le portfolio"
                  >
                    <LinkIcon className="h-5 w-5" />
                  </button>
                )}
              </div>
              {creator.status !== 'pending' && (
                <Badge className={`flex items-center space-x-1 ${getStatusColor(creator.status)}`} data-testid="badge-status">
                  {getStatusIcon(creator.status)}
                  <span className="capitalize">{creator.status}</span>
                </Badge>
              )}
            </div>
            
            <div className="flex-1 space-y-4">
              <div>
                <div className="flex items-center gap-3 flex-wrap mb-2">
                  <h1 className="text-3xl font-bold text-foreground" data-testid="text-creator-name">
                    {creator.name}
                  </h1>
                  {creator.isRedFlag && (
                    <RedFlagBadge 
                      reason={creator.redFlagReason} 
                      note={creator.redFlagNote}
                    />
                  )}
                  <Button
                    variant={creator.isRedFlag ? "destructive" : "outline"}
                    size="sm"
                    onClick={() => setShowRedFlagModal(true)}
                    data-testid="button-redflag-creator"
                  >
                    <Flag className="h-4 w-4 mr-1" />
                    {creator.isRedFlag ? "Modifier le signal" : "Signaler"}
                  </Button>
                  {/* Accès portail créateur */}
                  {creator.userId ? (
                    <div className="flex flex-col gap-1 w-full mt-1">
                      <Badge variant="outline" className="gap-1 text-emerald-600 border-emerald-200 bg-emerald-50 self-start">
                        <KeyRound className="h-3 w-3" />
                        Accès portail actif
                      </Badge>
                      {resetAccess ? (
                        <div className="flex flex-col gap-1 p-2 rounded-lg bg-amber-50 border border-amber-200 text-xs w-full">
                          <p className="font-semibold text-amber-700 flex items-center gap-1">
                            <KeyRound className="h-3 w-3" />
                            Nouveaux identifiants — à transmettre au créateur
                          </p>
                          <p className="text-foreground">Email : <span className="font-mono font-medium">{resetAccess.email}</span></p>
                          <p className="text-foreground">Mot de passe : <span className="font-mono font-medium">{resetAccess.tempPassword}</span></p>
                        </div>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleResetPassword}
                          disabled={resettingPassword}
                          className="gap-1 border-amber-200 text-amber-700 hover:bg-amber-50 text-xs h-7"
                        >
                          <KeyRound className="h-3 w-3" />
                          {resettingPassword ? 'Génération…' : 'Réinitialiser mot de passe'}
                        </Button>
                      )}
                    </div>
                  ) : portalAccess ? (
                    <div className="flex flex-col gap-1 p-2 rounded-lg bg-violet-50 border border-violet-200 text-xs w-full mt-1">
                      <p className="font-semibold text-violet-700 flex items-center gap-1">
                        <KeyRound className="h-3 w-3" />
                        {portalAccess.linked ? 'Compte existant lié' : 'Accès créé — noter ces identifiants'}
                      </p>
                      <p className="text-foreground">Email : <span className="font-mono font-medium">{portalAccess.email}</span></p>
                      {portalAccess.tempPassword && (
                        <p className="text-foreground">Mot de passe : <span className="font-mono font-medium">{portalAccess.tempPassword}</span></p>
                      )}
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCreatePortalAccess}
                      disabled={creatingAccess}
                      className="gap-1 border-violet-200 text-violet-700 hover:bg-violet-50"
                    >
                      <KeyRound className="h-3.5 w-3.5" />
                      {creatingAccess ? 'Création…' : 'Créer accès portail'}
                    </Button>
                  )}
                </div>
                {creator.rating !== undefined && creator.rating !== null && creator.rating > 0 && (
                  <div className="flex items-center space-x-1 mb-3">
                    <Star className="h-5 w-5 text-warning fill-current" />
                    <span className="font-semibold" data-testid="text-rating">{creator.rating}/5</span>
                  </div>
                )}
              </div>
              
              {/* Tarifs et délais intégrés - Grille flexible */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {/* Tarif vidéo */}
                <div className="p-3 rounded-lg bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 border border-primary/20 dark:border-blue-800" data-testid="pricing-video-rate">
                  <div className="flex items-center space-x-1.5 mb-1">
                    <Video className="h-3.5 w-3.5 text-primary dark:text-primary/70" />
                    <span className="text-xs font-medium text-blue-900 dark:text-blue-100">Vidéo</span>
                  </div>
                  <div className="text-lg font-bold text-blue-700 dark:text-primary/70">
                    {formatPrice(creator.videoRate)}
                  </div>
                </div>
                
                {/* Hook supplémentaire */}
                <div className="p-3 rounded-lg bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-950 dark:to-purple-900 border border-primary/20 dark:border-purple-800" data-testid="pricing-hook-rate">
                  <div className="flex items-center space-x-1.5 mb-1">
                    <Play className="h-3.5 w-3.5 text-primary dark:text-purple-400" />
                    <span className="text-xs font-medium text-purple-900 dark:text-purple-100">Hook</span>
                  </div>
                  <div className="text-lg font-bold text-purple-700 dark:text-purple-300">
                    {formatPrice(creator.hookRate)}
                  </div>
                </div>

                {/* Délai moyen - Affiché si renseigné avec traduction automatique EN→FR */}
                {creator.averageDelay && creator.averageDelay.trim() && (
                  <div className="p-3 rounded-lg bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-950 dark:to-orange-900 border border-warning/20 dark:border-orange-800" data-testid="availability-average-delay">
                    <div className="flex items-center space-x-1.5 mb-1">
                      <Clock className="h-3.5 w-3.5 text-warning dark:text-orange-400" />
                      <span className="text-xs font-medium text-orange-900 dark:text-orange-100">Délai</span>
                    </div>
                    <div className="text-sm font-bold text-orange-700 dark:text-orange-300">
                      {translateDelay(creator.averageDelay)}
                    </div>
                  </div>
                )}
              </div>
                {creator.availability && creator.availability.trim() && (
                  <div className="p-3 rounded-lg bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-950 dark:to-emerald-900 border border-emerald-200 dark:border-emerald-800" data-testid="pricing-availability">
                    <div className="flex items-center space-x-1.5 mb-1">
                      <CheckCircle className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                      <span className="text-xs font-medium text-emerald-900 dark:text-emerald-100">Disponibilité</span>
                    </div>
                    <div className="text-sm font-bold text-emerald-700 dark:text-emerald-300">
                      {creator.availability}
                    </div>
                  </div>
                )}

              {/* Grille tarifaire complète */}
              {creator.rates && creator.rates.trim() && (
                <div className="p-3 rounded-lg bg-muted/50 border border-border space-y-1" data-testid="pricing-rates">
                  <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                    <Euro className="h-3.5 w-3.5" />
                    Grille tarifaire complète
                  </p>
                  <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">{creator.rates}</p>
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                {creator.createdAt && (
                  <Badge variant="outline" className="flex items-center space-x-1">
                    <Calendar className="h-3 w-3" />
                    <span>Membre depuis {formatDate(creator.createdAt)}</span>
                  </Badge>
                )}
                {creator.country && (
                  <Badge variant="outline" className="flex items-center space-x-1">
                    <MapPin className="h-3 w-3" />
                    <span>{creator.country}</span>
                  </Badge>
                )}
                {creator.location && (
                  <Badge variant="outline" className="flex items-center space-x-1">
                    <Building2 className="h-3 w-3" />
                    <span>{creator.location}</span>
                  </Badge>
                )}
                {creator.gender && (
                  <Badge variant="outline" className="flex items-center space-x-1">
                    <User className="h-3 w-3" />
                    <span>{creator.gender === 'femme' ? 'Femme' : creator.gender === 'homme' ? 'Homme' : 'Autre'}</span>
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contact compact */}
      <Card className="border-l-4 border-l-blue-500 shadow-sm">
        <CardHeader className="bg-primary/10/50 dark:bg-blue-950/20 py-3">
          <CardTitle className="flex items-center space-x-2 text-blue-900 dark:text-blue-100 text-base">
            <Mail className="h-4 w-4" />
            <span>Informations de contact</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {/* Email */}
            <div className="flex items-center justify-between p-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors" data-testid="contact-email">
              <div className="flex items-center space-x-2 flex-1 min-w-0">
                <Mail className="h-4 w-4 text-primary dark:text-primary/70 flex-shrink-0" />
                <p className="text-sm font-medium break-all">{creator.email}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleCopy(creator.email, "Email")}
                className="flex-shrink-0 ml-2 h-7 w-7 p-0"
                data-testid="button-copy-email"
              >
                <Copy className="h-3 w-3" />
              </Button>
            </div>
            
            {/* Téléphone */}
            {creator.phone && (
              <div className="flex items-center justify-between p-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors" data-testid="contact-phone">
                <div className="flex items-center space-x-2 flex-1 min-w-0">
                  <Phone className="h-4 w-4 text-success dark:text-success/70 flex-shrink-0" />
                  <p className="text-sm font-medium">{creator.phone}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleCopy(creator.phone!, "Téléphone")}
                  className="flex-shrink-0 ml-2 h-7 w-7 p-0"
                  data-testid="button-copy-phone"
                >
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
            )}
            
            {/* Adresse postale */}
            {creator.addressPostal && (
              <div className="flex items-center justify-between p-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors md:col-span-2" data-testid="contact-address">
                <div className="flex items-center space-x-2 flex-1 min-w-0">
                  <MapPin className="h-4 w-4 text-primary dark:text-purple-400 flex-shrink-0" />
                  <p className="text-sm font-medium break-words whitespace-pre-wrap">{creator.addressPostal}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleCopy(creator.addressPostal!, "Adresse")}
                  className="flex-shrink-0 ml-2 h-7 w-7 p-0"
                >
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
            )}

            {/* Réseaux sociaux - style uniforme */}
            {creator.socialLinks && Object.entries(creator.socialLinks).map(([platform, url]) => (
              <div 
                key={platform}
                className="flex items-center justify-between p-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors md:col-span-2"
                data-testid={`contact-social-${platform}`}
              >
                <div className="flex items-center space-x-2 flex-1 overflow-hidden">
                  <LinkIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <div className="flex-1 overflow-hidden">
                    <p className="text-xs text-muted-foreground capitalize leading-none mb-0.5">{platform}</p>
                    <p className="text-sm font-medium break-all overflow-wrap-anywhere">{url}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-1 flex-shrink-0 ml-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleCopy(url, platform)}
                    className="h-7 w-7 p-0"
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleExternalLink(url)}
                    className="h-7 w-7 p-0"
                    data-testid={`social-link-${platform}`}
                  >
                    <ExternalLink className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Profil & Compétences */}
      <Card className="border-l-4 border-l-violet-500 shadow-sm">
        <CardHeader className="bg-violet-50/50 dark:bg-violet-950/20 py-3">
          <CardTitle className="flex items-center space-x-2 text-violet-900 dark:text-violet-100 text-base">
            <Sparkles className="h-4 w-4" />
            <span>Profil &amp; Compétences</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 space-y-5">

          {/* Bio */}
          {creator.bio && (
            <div data-testid="text-bio" className="p-3 rounded-xl bg-muted/40 border border-border/50">
              <p className={`text-muted-foreground text-sm leading-relaxed${bioExpanded ? '' : ' line-clamp-3'}`}>
                {creator.bio}
              </p>
              {creator.bio.length > 180 && (
                <button
                  onClick={() => setBioExpanded(v => !v)}
                  className="text-xs text-primary hover:underline mt-1 flex items-center gap-0.5"
                >
                  {bioExpanded ? <><ChevronUp className="h-3 w-3" />Voir moins</> : <><ChevronDown className="h-3 w-3" />Voir plus</>}
                </button>
              )}
            </div>
          )}

          {/* Informations personnelles */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div className="flex items-center space-x-2 p-2 rounded-lg bg-muted/50" data-testid="profile-age">
              <Calendar className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  Age / Naissance
                  {isBirthdaySoon(creator.birthDate) && (
                    <span title="Anniversaire dans moins de 14 jours !">🎂</span>
                  )}
                </p>
                <p className="text-sm font-semibold">
                  {creator.age ? `${creator.age} ans` : "Non spécifié"}
                </p>
                {creator.birthDate && (
                  <p className="text-xs text-muted-foreground">{formatBirthDate(creator.birthDate)}</p>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-2 p-2 rounded-lg bg-muted/50" data-testid="profile-country">
              <MapPin className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <div>
                <p className="text-xs text-muted-foreground">Pays</p>
                <p className="text-sm font-semibold">{creator.country || "Non spécifié"}</p>
              </div>
            </div>

            <div className="flex items-center space-x-2 p-2 rounded-lg bg-muted/50" data-testid="profile-gender">
              <User className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <div>
                <p className="text-xs text-muted-foreground">Genre</p>
                <p className="text-sm font-semibold">
                  {creator.gender === 'femme' ? 'Femme' : creator.gender === 'homme' ? 'Homme' : creator.gender === 'autre' ? 'Autre' : 'Non spécifié'}
                </p>
              </div>
            </div>

            {creator.pets && creator.pets.length > 0 && (
              <div className="flex items-center space-x-2 p-2 rounded-lg bg-muted/50" data-testid="profile-pets">
                <Heart className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground">Animaux</p>
                  <div className="flex flex-wrap gap-1 mt-0.5">
                    {creator.pets.map((pet, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">{pet}</Badge>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center space-x-2 p-2 rounded-lg bg-muted/50 col-span-2 md:col-span-1" data-testid="profile-languages">
              <Languages className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground mb-1">Langues</p>
                <div className="flex flex-wrap gap-1">
                  {creator.languages && creator.languages.length > 0 ? (
                    creator.languages.map((language, index) => (
                      <Badge key={index} variant="secondary" className="text-xs">
                        {language}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-sm text-muted-foreground">Aucune</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Plateformes actives */}
          {creator.platforms && creator.platforms.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                <Globe className="h-3.5 w-3.5" />Plateformes
              </p>
              <div className="flex flex-wrap gap-2" data-testid="professional-platforms">
                {creator.platforms.map((platform, index) => {
                  const styles: Record<string, string> = {
                    instagram: "bg-gradient-to-r from-purple-500 to-pink-500 text-white",
                    tiktok: "bg-zinc-900 text-white dark:bg-white dark:text-zinc-900",
                    youtube: "bg-red-600 text-white",
                    pinterest: "bg-red-500 text-white",
                    facebook: "bg-blue-600 text-white",
                    linkedin: "bg-blue-700 text-white",
                    snapchat: "bg-yellow-400 text-zinc-900",
                    twitter: "bg-zinc-900 text-white",
                    x: "bg-zinc-900 text-white",
                    twitch: "bg-purple-600 text-white",
                  };
                  const style = styles[platform.toLowerCase()] ?? "bg-muted text-muted-foreground";
                  return (
                    <span key={index} className={`px-3 py-1 rounded-full text-xs font-semibold ${style}`}>
                      {platform.charAt(0).toUpperCase() + platform.slice(1)}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* Spécialités */}
          <div className="space-y-2" data-testid="professional-specialties">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
              <Award className="h-3.5 w-3.5" />Spécialités
            </p>
            <div className="flex flex-wrap gap-2">
              {creator.specialties && creator.specialties.length > 0 ? (
                creator.specialties.map((specialty, index) => {
                  const colors = [
                    "bg-pink-50 dark:bg-pink-950/40 text-pink-700 dark:text-pink-300 border border-pink-200 dark:border-pink-800",
                    "bg-orange-50 dark:bg-orange-950/40 text-orange-700 dark:text-orange-300 border border-orange-200 dark:border-orange-800",
                    "bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800",
                    "bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800",
                    "bg-teal-50 dark:bg-teal-950/40 text-teal-700 dark:text-teal-300 border border-teal-200 dark:border-teal-800",
                    "bg-cyan-50 dark:bg-cyan-950/40 text-cyan-700 dark:text-cyan-300 border border-cyan-200 dark:border-cyan-800",
                    "bg-violet-50 dark:bg-violet-950/40 text-violet-700 dark:text-violet-300 border border-violet-200 dark:border-violet-800",
                    "bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-800",
                  ];
                  return (
                    <span key={index} className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[index % colors.length]}`}>
                      {specialty}
                    </span>
                  );
                })
              ) : (
                <span className="text-sm text-muted-foreground italic">Aucune spécialité renseignée</span>
              )}
            </div>
          </div>

          {/* Expérience UGC */}
          {creator.ugcExperience && (
            <div className="flex items-center gap-3 flex-wrap" data-testid="professional-ugc-experience">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" />Expérience UGC
              </p>
              {(() => {
                const v = creator.ugcExperience.toLowerCase();
                const levelStyle =
                  v.includes("6 mois") || v.includes("6mois") || v.includes("débutant")
                    ? "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700"
                    : v.includes("1") && (v.includes("an") || v.includes("year"))
                    ? "bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border border-blue-300 dark:border-blue-700"
                    : v.includes("2") && (v.includes("an") || v.includes("year"))
                    ? "bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300 border border-violet-300 dark:border-violet-700"
                    : v.includes("3") || v.includes("4") || v.includes("5") || v.includes("plus")
                    ? "bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 border border-amber-300 dark:border-amber-700"
                    : "bg-muted text-muted-foreground border border-border";
                return (
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${levelStyle}`}>
                    {creator.ugcExperience}
                  </span>
                );
              })()}
            </div>
          )}

          {/* Expérience professionnelle */}
          {creator.experience && creator.experience.trim() && (
            <div className="space-y-2" data-testid="professional-experience">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                <Briefcase className="h-3.5 w-3.5" />Expérience
              </p>
              <div className="p-3 rounded-xl bg-muted/40 border border-border/50">
                <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{creator.experience}</p>
              </div>
            </div>
          )}

          {/* Types de contenu */}
          <div className="space-y-2" data-testid="professional-content-types">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
              <Camera className="h-3.5 w-3.5" />Types de contenu
            </p>
            <div className="flex flex-wrap gap-2">
              {creator.contentTypes && creator.contentTypes.length > 0 ? (
                creator.contentTypes.map((type, index) => {
                  const t = type.toLowerCase();
                  const icon = t.includes("vidéo") || t.includes("video")
                    ? <Video className="h-3 w-3" />
                    : t.includes("photo") || t.includes("image")
                    ? <Camera className="h-3 w-3" />
                    : t.includes("reel") || t.includes("story") || t.includes("short")
                    ? <Play className="h-3 w-3" />
                    : <Sparkles className="h-3 w-3" />;
                  return (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800"
                    >
                      {icon}
                      {type}
                    </span>
                  );
                })
              ) : (
                <span className="text-sm text-muted-foreground italic">Aucun type renseigné</span>
              )}
            </div>
          </div>

          {/* Équipement */}
          {creator.equipment && creator.equipment.trim() && (
            <div className="space-y-2" data-testid="professional-equipment">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                <Settings className="h-3.5 w-3.5" />Équipement
              </p>
              <div className="p-3 rounded-xl bg-amber-50/60 dark:bg-amber-950/20 border border-amber-200/60 dark:border-amber-800/30">
                <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{creator.equipment}</p>
              </div>
            </div>
          )}

          {/* Choses à savoir */}
          {creator.thingsToKnow && creator.thingsToKnow.trim() && (
            <div className="space-y-2" data-testid="professional-things-to-know">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                <AlertCircle className="h-3.5 w-3.5" />Choses à savoir
              </p>
              <div className="p-3 rounded-xl bg-rose-50/60 dark:bg-rose-950/20 border border-rose-200/60 dark:border-rose-800/30">
                <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{creator.thingsToKnow}</p>
              </div>
            </div>
          )}

        </CardContent>
      </Card>

      {/* Portfolio compact - Toujours affiché */}
      <Card ref={portfolioRef} className="border-l-4 border-l-indigo-500 shadow-sm">
        <CardHeader className="bg-primary/10/50 dark:bg-indigo-950/20 py-3">
          <CardTitle className="flex items-center space-x-2 text-indigo-900 dark:text-indigo-100 text-base">
            <Folder className="h-4 w-4" />
            <span>Portfolio</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 space-y-3">
          {/* Lien Portfolio Site Web */}
          {creator.website ? (
            <a
              href={ensureAbsoluteUrl(creator.website)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between p-4 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-950/30 dark:to-purple-950/30 hover:from-indigo-100 hover:to-purple-100 dark:hover:from-indigo-900/40 dark:hover:to-purple-900/40 border border-primary/20 dark:border-indigo-800 rounded-xl transition-all duration-200 group shadow-sm hover:shadow-md"
              data-testid="portfolio-link"
            >
              <div className="flex items-center space-x-3 flex-1 min-w-0">
                <div className="flex-shrink-0 w-10 h-10 bg-card dark:bg-indigo-900/50 rounded-lg flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform">
                  <LinkIcon className="h-5 w-5 text-primary dark:text-indigo-400" />
                </div>
                <div className="flex flex-col min-w-0 overflow-hidden">
                  <span className="text-sm font-semibold text-indigo-900 dark:text-indigo-100 group-hover:text-primary dark:group-hover:text-indigo-200">
                    Voir le portfolio
                  </span>
                  <span className="text-xs text-primary/70 dark:text-indigo-400/70 truncate">
                    {(() => {
                      try {
                        return new URL(creator.website).hostname;
                      } catch {
                        return creator.website.replace(/^https?:\/\//, '').split('/')[0];
                      }
                    })()}
                  </span>
                </div>
              </div>
              <ExternalLink className="h-5 w-5 flex-shrink-0 text-primary dark:text-indigo-400 group-hover:translate-x-1 transition-transform" />
            </a>
          ) : (
            <div className="flex items-center p-4 text-sm text-muted-foreground bg-muted/30 rounded-xl border border-dashed" data-testid="portfolio-link">
              <LinkIcon className="h-4 w-4 mr-2 flex-shrink-0" />
              <span>Pas de site portfolio disponible</span>
            </div>
          )}

          {/* Vidéo phare */}
          {creator.prideVideoUrl && (
            <div className="space-y-2" data-testid="pride-video">
              <p className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5 text-violet-500" />
                Vidéo phare
              </p>
              <div className="relative rounded-xl overflow-hidden bg-muted" style={{ maxHeight: 320 }}>
                <video
                  src={creator.prideVideoUrl}
                  controls
                  muted
                  playsInline
                  className="w-full object-cover"
                  style={{ maxHeight: 320 }}
                />
              </div>
              {creator.prideVideoConsent && (
                <p className="text-xs text-emerald-600 flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" />
                  Consentement RGPD accordé
                  {creator.prideVideoFeatured && ' · Mise en avant sur l\'agence'}
                </p>
              )}
              {creator.prideVideoCategory && (
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Folder className="h-3 w-3" />
                  Catégorie : <span className="font-medium text-foreground">{creator.prideVideoCategory}</span>
                </p>
              )}
            </div>
          )}

          {/* Vidéos Portfolio - liens simples */}
          {creator.portfolioVideos && creator.portfolioVideos.length > 0 && (
            <div className="space-y-2" data-testid="portfolio-videos">
              {creator.portfolioVideos.map((url, index) => (
                <a
                  key={index}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-3 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-950/30 dark:to-pink-950/30 hover:from-purple-100 hover:to-pink-100 dark:hover:from-purple-900/40 dark:hover:to-pink-900/40 border border-primary/20 dark:border-purple-800 rounded-lg transition-all duration-200 group shadow-sm hover:shadow"
                >
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-card dark:bg-purple-900/50 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Video className="h-4 w-4 text-primary dark:text-purple-400" />
                    </div>
                    <span className="text-sm font-medium text-purple-900 dark:text-purple-100 group-hover:text-purple-700 dark:group-hover:text-purple-200">
                      Vidéo portfolio {index + 1}
                    </span>
                  </div>
                  <ExternalLink className="h-4 w-4 flex-shrink-0 text-primary dark:text-purple-400 group-hover:translate-x-1 transition-transform" />
                </a>
              ))}
            </div>
          )}

          {/* Exemples de travail */}
          {creator.exampleUrls && creator.exampleUrls.length > 0 && (
            <div className="space-y-2" data-testid="portfolio-examples">
              <p className="text-sm font-medium text-muted-foreground">Exemples de travail</p>
              <div className="space-y-2">
                {creator.exampleUrls.map((url, index) => (
                  <a
                    key={index}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-3 bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-cyan-950/30 dark:to-blue-950/30 hover:from-cyan-100 hover:to-blue-100 dark:hover:from-cyan-900/40 dark:hover:to-blue-900/40 border border-primary/20 dark:border-cyan-800 rounded-lg transition-all duration-200 group shadow-sm hover:shadow"
                  >
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <div className="flex-shrink-0 w-8 h-8 bg-card dark:bg-cyan-900/50 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                        <LinkIcon className="h-4 w-4 text-primary dark:text-cyan-400" />
                      </div>
                      <span className="text-sm font-medium text-cyan-900 dark:text-cyan-100 group-hover:text-cyan-700 dark:group-hover:text-cyan-200 truncate">
                        Exemple {index + 1}
                      </span>
                    </div>
                    <ExternalLink className="h-4 w-4 flex-shrink-0 text-primary dark:text-cyan-400 group-hover:translate-x-1 transition-transform" />
                  </a>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section Collaborations */}
      <Card className="border-l-4 border-l-green-500 shadow-sm">
        <CardHeader className="bg-green-50/50 dark:bg-green-950/20 py-3">
          <CardTitle className="flex items-center space-x-2 text-green-900 dark:text-green-100 text-base">
            <History className="h-4 w-4" />
            <span>
              Collaborations
              {assignmentsData && assignmentsData.total > 0 && (
                <span className="ml-1 text-sm font-normal text-muted-foreground">
                  ({assignmentsData.total})
                </span>
              )}
            </span>
            {creator.hasBeenContacted && (
              <CheckCircle2 className="h-4 w-4 text-green-500 ml-auto" />
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          {!assignmentsData || assignmentsData.total === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-2">Aucune collaboration pour l'instant</p>
          ) : (
            <div className="space-y-2">
              {assignmentsData.assignments.map((assignment) => {
                const date = assignment.contactedAt ? new Date(assignment.contactedAt) : null;
                const now = new Date();
                const diffMs = date ? now.getTime() - date.getTime() : 0;
                const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                let relativeDate = "";
                if (diffDays === 0) relativeDate = "Aujourd'hui";
                else if (diffDays === 1) relativeDate = "Hier";
                else if (diffDays < 30) relativeDate = `Il y a ${diffDays} j`;
                else if (diffDays < 365) relativeDate = `Il y a ${Math.floor(diffDays / 30)} mois`;
                else relativeDate = `Il y a ${Math.floor(diffDays / 365)} an${Math.floor(diffDays / 365) > 1 ? 's' : ''}`;

                return (
                  <div key={assignment.id} className="flex items-center gap-3 p-2 rounded-lg bg-muted/50 text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{assignment.script.project.title}</p>
                      <p className="text-xs text-muted-foreground truncate">{assignment.script.title}</p>
                    </div>
                    <span className="text-xs text-muted-foreground flex-shrink-0">{relativeDate}</span>
                  </div>
                );
              })}

              {assignmentsData.total > 3 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full text-xs text-muted-foreground"
                  onClick={() => setShowAllAssignments(prev => !prev)}
                >
                  {showAllAssignments ? (
                    <>
                      <ChevronUp className="h-3 w-3 mr-1" />
                      Réduire
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-3 w-3 mr-1" />
                      Voir les {assignmentsData.total - 3} autres
                    </>
                  )}
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal vidéo */}
      <Dialog open={showVideoModal} onOpenChange={setShowVideoModal}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Aperçu vidéo</DialogTitle>
          </DialogHeader>
          {selectedVideo && (
            <div className="space-y-4">
              <AspectRatio ratio={16 / 9}>
                <iframe
                  src={parseVideoUrl(selectedVideo).embedUrl}
                  className="w-full h-full rounded-lg"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </AspectRatio>
              <div className="flex items-center justify-between">
                <Button
                  variant="outline"
                  onClick={() => handleExternalLink(selectedVideo)}
                  className="flex items-center space-x-2"
                >
                  <ExternalLink className="h-4 w-4" />
                  <span>Ouvrir dans un nouvel onglet</span>
                </Button>
                <Button variant="secondary" onClick={() => setShowVideoModal(false)}>
                  Fermer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal RedFlag */}
      <RedFlagModal
        open={showRedFlagModal}
        onOpenChange={setShowRedFlagModal}
        creatorId={creator.id}
        creatorName={creator.name || "Ce créateur"}
        isCurrentlyFlagged={creator.isRedFlag || false}
        currentReason={creator.redFlagReason}
        currentNote={creator.redFlagNote}
      />
    </div>
  );
}
